import type { AppliedDeposit } from '../services/appliedDeposit';
import type { PaymentSelection, ScheduleRow } from '../types/schedule.types';

export interface InstalmentRun {
    readonly count: number;
    readonly paymentMinor: number;
}

const isDealerCollectedDeposit = (value: unknown): boolean =>
    typeof value === 'number' && Number.isInteger(value) && value > 0;

const isInstalmentAmount = (value: unknown): boolean =>
    typeof value === 'number' && Number.isInteger(value) && value > 0;

const selectInstalmentRows = (
    selection: PaymentSelection,
    appliedDeposit: AppliedDeposit,
): readonly ScheduleRow[] | null => {
    const { params, snapshot } = selection;
    if (params.hasDeposit !== true) {
        return snapshot.schedule;
    }
    if (isDealerCollectedDeposit(params.dealerCollectedDepositAmount)) {
        return snapshot.schedule;
    }
    if (appliedDeposit.depositRowConfirmed) {
        return snapshot.schedule.slice(1);
    }
    return null;
};

/** Null when row-0 identity is unknown or any row is not a positive integer minor-unit payment. */
export const confirmedInstalmentRows = (
    selection: PaymentSelection,
    appliedDeposit: AppliedDeposit,
): readonly ScheduleRow[] | null => {
    const rows = selectInstalmentRows(selection, appliedDeposit);
    if (rows === null || rows.length === 0) {
        return null;
    }
    return rows.every((row) => isInstalmentAmount(row.payment)) ? rows : null;
};

export const groupEqualPaymentRuns = (rows: readonly ScheduleRow[]): InstalmentRun[] =>
    rows.reduce<InstalmentRun[]>((runs, row) => {
        const last = runs[runs.length - 1];
        if (last && last.paymentMinor === row.payment) {
            return [...runs.slice(0, -1), { count: last.count + 1, paymentMinor: last.paymentMinor }];
        }
        return [...runs, { count: 1, paymentMinor: row.payment }];
    }, []);
